// RCE → legacy Profile Manager bridge.
//
// Root cause this exists to fix: CreateKillSheetFromOpStateModal (and the
// standalone Kill Sheet Wizard behind it, lib/killsheet/sessionStore.js)
// only ever looked at the deprecated Profile Manager (lib/profiles/storage.js,
// keys `rsc.profiles.*.v1`). Every current-generation screen (Well Profile
// Hub, BHA Hub, Mud Program Hub, Rig Data Sheet) writes to a completely
// different set of canonical stores (rsc.wells.v1, rsc.bha.v1,
// rsc.mudPrograms.v1, rsc.rigsheets.v1), which the legacy store never sees.
// So a user who set up their data the current (canonical) way always saw
// "No <kind> Profile found" for every kind, even with real data on file.
//
// This module makes that real canonical data usable by the legacy Kill
// Sheet flow WITHOUT changing the canonical stores, the legacy schema, or
// the composer/validator that the standalone Kill Sheet already trusts:
//
//   1. listRceCandidates(kind) enumerates "virtual" profiles built live
//      from canonical data. They are never written to storage on their own
//      — just candidates a picker can show.
//   2. materializeRceCandidate(kind, id) turns one of those virtual
//      profiles into a REAL legacy profile row (via profiles/storage.js's
//      own createProfile()), snapshotting the canonical data at the
//      moment of Kill Sheet creation — consistent with this app's existing
//      "kill sheets are snapshot-locked" rule (see profiles/storage.js's
//      header comment). The resulting row is a normal, editable legacy
//      profile like any other; nothing downstream needs to know it was
//      bridged from RCE.
//
// Every candidate is pre-filtered so materializing it is guaranteed to
// satisfy VALIDATORS[kind] in ./schemas.js — a kind of data this bridge
// cannot safely reconstruct (e.g. a casing string with no Casing Library
// record, so no real inner diameter) is left OUT of the candidate list
// rather than filled with a fabricated number. This is a well-control
// engineering tool: a missing value must stay missing, never guessed.
//
// Field-by-field source notes:
//   * Well current MD/TVD/hole size/casing-shoe depth all resolve through
//     the same RCE resolver (lib/rce/resolver.js) that Operational State's
//     own "Apply Available Values" already uses for these exact fields —
//     reused here rather than re-derived, so behavior never drifts between
//     the two features.
//   * Casing string OD/ID come from the Casing Library record
//     (lib/reflibs) referenced by the well's casing-program row, NOT the
//     row itself — the row alone only carries a nominal size label, no true
//     ID. A row with no library record (or a record missing OD/ID) is
//     skipped rather than guessed.
//   * Subsea riser/choke-line GEOMETRY (riser capacity, choke-line length)
//     has no canonical source at all — the Rig Data Sheet only stores
//     total well-control VOLUMES (bbl), not a length+ID breakdown — so
//     those two fields are left blank on the materialized Well Profile for
//     the user to fill in via Edit, same as before this bridge existed.
//     Marine riser ID DOES have a canonical source (Rig Data Sheet
//     subsea.riserId) and is mapped.
//   * Pump "displacement" is computed from geometry (liner/stroke/
//     efficiency/rod) via the same computePumpDisplacement() helper the
//     Rig Data Sheet itself uses (lib/calc/pumpDisplacement.js) — the
//     legacy Kill Sheet validator checks pump.pumps[idx].displacement
//     directly and does not derive it from geometry on its own, so it must
//     be filled in here rather than left blank.

import { resolve } from "@/lib/rce/resolver";
import { listWellProfiles, getWellProfile } from "@/lib/well/storage";
import { listBhasForWell } from "@/lib/bha/storage";
import { listRigSheets, getRigSheet } from "@/lib/rigsheet/storage";
import { wellStackMode } from "@/lib/well/schema";
import { findById } from "@/lib/reflibs/lookup";
import { computePumpDisplacement } from "@/lib/calc/pumpDisplacement";
import { createProfile } from "./storage";
import { CASING_STRING_KINDS } from "./schemas";

const RCE_PREFIX = "rce:";

const _isNum   = (v) => v !== "" && v != null && Number.isFinite(Number(v));
const _posNum  = (v) => _isNum(v) && Number(v) > 0;
const _num     = (v) => (v === "" || v == null ? "" : v);
const _wellLabel = (w) => w.wellName || "(unnamed well)";

// ---------------------------------------------------------------------------
// WELL — one candidate per Well Profile that has a resolvable current MD/TVD
// (both required, positive numbers by the legacy Well Profile validator).
// ---------------------------------------------------------------------------
function _wellMdTvd(w) {
  const ctx = { wellId: w.id, bhaId: w.activeBhaId || "" };
  return {
    md:  resolve("well.currentMdFt", ctx).value,
    tvd: resolve("well.currentTvdFt", ctx).value,
  };
}

function _wellCandidates() {
  return listWellProfiles()
    .filter((w) => { const { md, tvd } = _wellMdTvd(w); return _posNum(md) && _posNum(tvd); })
    .map((w) => ({
      id: `${RCE_PREFIX}well:${w.id}`,
      label: `${_wellLabel(w)} (Engineering Setup)`,
      version: "live",
    }));
}

function _materializeWell(wellId) {
  const w = getWellProfile(wellId);
  if (!w) throw new Error("Well Profile not found in Engineering Setup.");
  const ctx = { wellId: w.id, bhaId: w.activeBhaId || "" };
  const { md, tvd } = _wellMdTvd(w);
  const holeSize = resolve("well.holeSizeIn", ctx).value;
  const shoeMd   = resolve("well.casingShoeMdFt", ctx).value;
  const shoeTvd  = resolve("well.casingShoeTvdFt", ctx).value;
  const lot      = resolve("well.lotPressurePsi", ctx).value;
  const lotMw    = resolve("well.mwAtLotPpg", ctx).value;
  const isSubsea = wellStackMode(w) === "subsea";
  // The Rig Data Sheet linked to this well (if any) supplies marine riser
  // ID; riser capacity / choke-line length have no canonical source (see
  // header note) and stay blank for the user to fill in on the profile.
  const rig = isSubsea ? listRigSheets().find((r) => r.payload?.linkedWellId === w.id) : null;

  return {
    label: _wellLabel(w),
    units: "IMP",
    rig: w.rigName || "",
    currentMd: _num(md), currentTvd: _num(tvd),
    holeSize: _num(holeSize),
    shoeMd: _num(shoeMd), shoeTvd: _num(shoeTvd),
    lotPressure: _num(lot), lotTestMudDensity: _num(lotMw),
    isSubsea,
    waterDepth: _num(w.waterDepthFt),
    riserIdIn: _num(rig?.payload?.subsea?.riserId),
    riserCapacity: "",
    chokeLineLength: "",
    chokeLineIdIn: _num(rig?.payload?.subsea?.chokeLineId),
    notes: "",
  };
}

// ---------------------------------------------------------------------------
// BHA — one candidate per canonical BHA record that has at least one
// component with a real (positive) length. Components without a length
// are dropped rather than materialized with a fabricated one.
// ---------------------------------------------------------------------------
const BHA_ROLE_TO_LEGACY_KIND = {
  bit: "Bit", motor: "Mud Motor", rss: "RSS", mwd: "MWD", lwd: "LWD",
  stabilizer: "Stabilizer", drill_collar: "Drill Collar", hwdp: "HWDP",
  drill_pipe: "Drill Pipe", crossover: "Crossover",
  jar: "Sub", shock_sub: "Sub", other: "Sub",
};

function _findBhaWithWell(bhaId) {
  for (const w of listWellProfiles()) {
    const hit = listBhasForWell(w.id).find((r) => r.id === bhaId);
    if (hit) return { bha: hit, well: w };
  }
  return null;
}

function _bhaCandidates() {
  const out = [];
  for (const w of listWellProfiles()) {
    for (const b of listBhasForWell(w.id)) {
      const hasUsableComponent = (b.components || []).some((c) => _posNum(c.lengthFt));
      if (!hasUsableComponent) continue;
      out.push({
        id: `${RCE_PREFIX}bha:${b.id}`,
        label: `${b.name || b.bhaNumber || "BHA"} (${_wellLabel(w)})`,
        version: "live",
      });
    }
  }
  return out;
}

function _materializeBha(bhaId) {
  const hit = _findBhaWithWell(bhaId);
  if (!hit) throw new Error("BHA not found in Engineering Setup.");
  const { bha, well } = hit;
  const components = (bha.components || [])
    .filter((c) => _posNum(c.lengthFt))
    .map((c) => ({
      kind: BHA_ROLE_TO_LEGACY_KIND[c.role] || "Sub",
      length: _num(c.lengthFt),
      odIn: _num(c.outerDiameter),
      idIn: _num(c.innerDiameter),
      weightLbFt: _num(c.weightPpf),
      grade: "",
      libId: c.refId || "",
    }));
  if (components.length === 0) throw new Error("This BHA has no component with a usable length.");
  return {
    label: bha.name || bha.bhaNumber || "BHA",
    units: "IMP",
    rig: well.rigName || "",
    components,
    notes: bha.notes || "",
  };
}

// ---------------------------------------------------------------------------
// CASING — one candidate per Well that has at least one casing-program row
// whose Casing Library record carries a real OD + ID. A row with no library
// record, or a record missing OD/ID, is skipped (never guessed).
// ---------------------------------------------------------------------------
function _casingRowsWithDims(w) {
  const rows = w.children?.casingProgram || [];
  const out = [];
  for (const r of rows) {
    const lib = r.casingRefId ? findById(r.casingRefId) : null;
    if (!lib || !_posNum(lib.outerDiameter) || !_posNum(lib.innerDiameter)) continue;
    out.push({ row: r, lib });
  }
  return out;
}

function _casingCandidates() {
  return listWellProfiles()
    .filter((w) => _casingRowsWithDims(w).length > 0)
    .map((w) => ({
      id: `${RCE_PREFIX}casing:${w.id}`,
      label: `${_wellLabel(w)} casing program (Engineering Setup)`,
      version: "live",
    }));
}

function _materializeCasing(wellId) {
  const w = getWellProfile(wellId);
  if (!w) throw new Error("Well Profile not found in Engineering Setup.");
  const dims = _casingRowsWithDims(w);
  if (dims.length === 0) throw new Error("No casing row on this well has a Casing Library record with OD/ID set.");
  const strings = dims.map(({ row, lib }) => ({
    kind: CASING_STRING_KINDS.includes(row.holeSection) ? row.holeSection : "Surface",
    odIn: _num(lib.outerDiameter),
    idIn: _num(lib.innerDiameter),
    weightLbFt: _num(lib.weight ?? row.weightPpf),
    grade: lib.grade || row.grade || "",
    libId: row.casingRefId,
  }));
  return {
    label: `${_wellLabel(w)} — Casing Program`,
    units: "IMP",
    rig: w.rigName || "",
    strings,
    notes: "",
  };
}

// ---------------------------------------------------------------------------
// MUD — one candidate per Well whose active Mud Program (or latest Daily
// Mud Report) resolves a real mud weight. Mirrors exactly what Operational
// State's own "Apply Available Values" already uses for mud.weightInPpg.
// ---------------------------------------------------------------------------
function _mudCandidates() {
  return listWellProfiles()
    .filter((w) => _posNum(resolve("mud.weightInPpg", { wellId: w.id }).value))
    .map((w) => ({
      id: `${RCE_PREFIX}mud:${w.id}`,
      label: `${_wellLabel(w)} mud (Engineering Setup)`,
      version: "live",
    }));
}

function _materializeMud(wellId) {
  const w = getWellProfile(wellId);
  if (!w) throw new Error("Well Profile not found in Engineering Setup.");
  const density = resolve("mud.weightInPpg", { wellId }).value;
  if (!_posNum(density)) throw new Error("No mud weight available from this well's active Mud Program.");
  return {
    label: `${_wellLabel(w)} — Mud`,
    units: "IMP",
    rig: w.rigName || "",
    density: _num(density),
    // Gradient / active surface volume have no canonical source anywhere in
    // RigSentinel today (same gap noted in OperationalStateProfileForm) —
    // left blank for manual entry rather than guessed.
    gradient: "",
    sg: "",
    activeSurfaceVolume: "",
    notes: "",
  };
}

// ---------------------------------------------------------------------------
// PUMP — one candidate per Rig Data Sheet that has at least one pump with
// complete geometry (liner + stroke + efficiency, plus rod diameter for a
// duplex pump) — the same completeness bar the legacy Pump Profile
// validator itself enforces.
// ---------------------------------------------------------------------------
function _pumpHasLegacyGeometry(p) {
  const hasGeom = _posNum(p.linerSize) && _posNum(p.strokeLength) && _posNum(p.efficiency);
  const needsRod = p.pumpType === "duplex";
  return hasGeom && (!needsRod || _posNum(p.rodDiameter));
}

function _pumpCandidates() {
  return listRigSheets()
    .filter((rs) => (rs.payload?.pumps || []).some(_pumpHasLegacyGeometry))
    .map((rs) => ({
      id: `${RCE_PREFIX}pump:${rs.id}`,
      label: `${rs.payload?.rigName || rs.label || "Rig"} pumps (Engineering Setup)`,
      version: "live",
    }));
}

function _materializePump(rigSheetId) {
  const rs = getRigSheet(rigSheetId);
  if (!rs) throw new Error("Rig Data Sheet not found in Engineering Setup.");
  const pumps = (rs.payload?.pumps || [])
    .filter(_pumpHasLegacyGeometry)
    .map((p) => {
      const mapped = {
        label: p.label || "Pump",
        pumpType: p.pumpType === "duplex" ? "duplex_da" : "triplex_sa",
        liner: _num(p.linerSize),
        stroke: _num(p.strokeLength),
        rodDia: _num(p.rodDiameter),
        efficiency: _num(p.efficiency),
        cylinders: p.pumpType === "duplex" ? "2" : "3",
        measuredStrokes: "", measuredVolume: "",
        spm: "", slowPumpPressure: "",
      };
      // The legacy Kill Sheet validator checks pump.pumps[idx].displacement
      // directly (it does not derive it from geometry itself), so compute it
      // here from the same geometry fields rather than leaving it blank.
      const disp = computePumpDisplacement(mapped, "IMP");
      mapped.displacement = disp != null ? String(disp) : "";
      return mapped;
    });
  if (pumps.length === 0) throw new Error("No pump on this Rig Data Sheet has complete geometry (liner/stroke/efficiency).");
  return {
    label: `${rs.payload?.rigName || rs.label || "Rig"} — Pumps`,
    units: "IMP",
    rig: rs.payload?.rigName || "",
    pumps,
    notes: "",
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
const CANDIDATE_BUILDERS = {
  well: _wellCandidates, bha: _bhaCandidates, casing: _casingCandidates,
  mud: _mudCandidates, pump: _pumpCandidates,
};
const MATERIALIZERS = {
  well: _materializeWell, bha: _materializeBha, casing: _materializeCasing,
  mud: _materializeMud, pump: _materializePump,
};

/** Virtual (never-persisted) candidates built live from canonical RCE data. */
export function listRceCandidates(kind) {
  const build = CANDIDATE_BUILDERS[kind];
  if (!build) return [];
  try { return build(); } catch { return []; }
}

export function isRceCandidateId(id) {
  return typeof id === "string" && id.startsWith(RCE_PREFIX);
}

/**
 * Turn one RCE-sourced candidate id into a real legacy profile row
 * (persisted via profiles/storage.js#createProfile — same path a user
 * hitting "+" on the Kill Sheet editor already goes through). Throws with
 * a human-readable message on failure; callers should surface it, not
 * swallow it.
 */
export function materializeRceCandidate(kind, candidateId) {
  const build = MATERIALIZERS[kind];
  if (!build) throw new Error(`Unsupported profile kind for Engineering Setup import: ${kind}`);
  const prefix = `${RCE_PREFIX}${kind}:`;
  if (!candidateId.startsWith(prefix)) throw new Error("Not an Engineering Setup candidate id.");
  const sourceId = candidateId.slice(prefix.length);
  const payload = build(sourceId);
  return createProfile(kind, payload);
}
